import { Injectable } from "@angular/core";
/**
 * One of common coding issues: how to prevent memory leaks that occur due to registering your function
 * to native timeout (setTimeout(xx, 1)?
 * In case of larger data sets and intensive navigation over the pages, memory leak
 * is quite noticeable.
 *
 * To help fighting this problem, let's introduce a wrapper class, that wraps access to timeout functions and helps you
 * to clean up things properly.
 * A bit of theory:
 * - setTimeout returns a number/which is a handle to your callback, registered in browser memory space.
 * - you might think that after firing your function, web browser removes reference to your callback? As of today, this is not
 *   the case. You must take care to call clearTimeout(handle) yourself. Here, the 'handle' is the number, that browser has
 *   returned to you when you called setTimeout().
 *
 * Implementation notes:
 * - in Angular, there is no concept of $scope. Therefore there is no easy way to detect, when this timeout wrapper object
 *   is going out-of-use and all registered timeout functions must be cleared.
 * - on the other hand, there are two different cases to use this wrapper: a) by components (which are created/destroyed often)
 *   and b) by services (which might be destroyed similar to components OR stay in memory forever/if singletons).
 * - In case of components, it is recommended to register this wrapper as 'provider' inside of @component annotation.
 *   By doing so we trust that Angular framework will instantiate the object once per component, and will call ngOnDestroy once
 *   the component (and its direct/private providers) are destroyed.
 *   See class TimeoutWrapperService/which should be as provider.
 * - In case of services, I recommend to construct timeout.wrapper object via 'new' operator and manage its lifecycle yourself
 *   (eg, if your service is singleton - then nothing needs to be destroyed ever. If your service will go out of scope often,
 *   you may call cleanup method on the object from ngOnDestroy of your service).
 *
 */
@Injectable()
export class TimeoutWrapper {
  constructor() {}

  /**
   * given function will be unregistered from global timeout after angular resolves Promise
   * Recommended for use in Service classes.
   */
  add(timeoutFunction, timeoutPeriod) {
    const handle = [-1];
    handle[0] = setTimeout(() => {
      try {
        timeoutFunction();
      } finally {
        this.clearTimeout(handle[0]);
      }
    }, timeoutPeriod) as any;
    return handle[0];
  }

  /**
   * Similarly to 'add' method, given timeout shall be unregistered from global timeout as soon as angular
   * resolves the Promise.
   * However, there are two additional benefits:
   * a) the method tries to locate 'previous' timeout function, added under the same 'key' - and in case
   *    such function exists, it is cancelled without waiting for timeout to be fired.
   * b) if timeoutwrapper is aware of scope, all timeout functions, registered via current method, will be
   *    cancelled on 'destroy' event.
   * Recommended for use from all Controller classes.
   */
  set(key, timeoutFunction, timeoutPeriod) {
    // cancel previous timeout, if such is found
    if (this[key]) {
      this.clearTimeout(this[key]);
    }
    const handle = [0];
    handle[0] = setTimeout(() => {
      try {
        timeoutFunction();
      } finally {
        this.clearTimeout(handle[0]);
        this[key] = undefined;
      }
    }, timeoutPeriod) as any;

    this[key] = handle[0];
    return handle[0];
  }

  /**
   * Added solely for unit testing purposes. We can spy on this method and verify timeout cancel has been called
   * with the right handle.
   */
  clearTimeout(handle) {
    clearTimeout(handle);
  }

  /**
   * Some use cases require more control over when and if timeout function should be fired.
   * This is an ability to cancel given function before it happened.
   */
  cancel(handle) {
    if (handle) {
      this.clearTimeout(handle);
    }
  }

  onDestroy() {
    Object.keys(this).forEach((key, index) => {
      const propValue = this[key];
      if (propValue && key !== "timeout") {
        this.clearTimeout(propValue);
      }
    });
  }
}
